import React, { useEffect, useContext } from 'react';
import CustomHelmet from 'components/CustomHelmet';
import {
  DEFAULT_THEME_COLOR,
} from 'appenv';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useSelector } from 'react-redux';
import {
  selectAllLiveWebinars,
  selectAllOnDemandWebinars,
  selectIsWebinarsLoading,
  selectAllRelevantTracks,
} from 'models/webinars';
import Routes from 'Routes';
import { TabsContextStateProvider, TabsContext } from 'components/TabsContext';
import TimeZoneSwitch from 'components/TimeZoneSwitch';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { selectLocale } from 'models/localization';
import { selectWebinarInterval, selectLocalizedEventName } from 'models/virtual_event';
import LiveListContainer from 'pages/webinar/live/LiveListContainer';
import { BREAKPOINT } from 'pages/webinar/constants';
import LiveWebinarTracksList from 'pages/webinar/live/LiveWebinarTracksList';
import XtraBreadcrumbs from 'components/XtraBreadcrumbs';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';
import OnDemandListContainer from './OnDemandListContainer';

const WEBINAR_TAB_KEYS = {
  AGENDA: 0,
  ON_DEMAND: 1,
};

const RootContainer = styled.div`
  height: 100%;
  overflow: hidden;

  @media (min-width: ${BREAKPOINT}px) {
    background-color: #FFF;
  }

  @media (max-width: ${BREAKPOINT}px) {
    background-color: #FFF;
  }
`;

const BreadcrumbTimeZoneContainer = styled.div`
  display: block;
`;

const PageWrapper = styled.div`
  position: relative;
  min-width: 100%;
  height: 100%;
  overflow: auto;
  justify-content: space-between;
  display: flex;
  flex-direction: row;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const PageContentContainer = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const StyledTabsContainer = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const CustomTabs = withStyles(() => ({
    root: {
      flexShrink: 0,
      [`@media (max-width: ${BREAKPOINT}px)`]: {
        backgroundColor: '#FFF',
      },
    },
    flexContainer: {
      borderBottom: '2px solid #DBDEE2',
    },
    indicator: {
      backgroundColor: themeColor || DEFAULT_THEME_COLOR,
      height: '4px',
    },
  }))(Tabs);
  return <CustomTabs {...props}>{children}</CustomTabs>;
};
StyledTabsContainer.propTypes = {
  children: PropTypes.node,
};
StyledTabsContainer.defaultProps = {
  children: null,
};

const StyledTab = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const CustomTab = withStyles(() => ({
    root: {
      color: '#A2AAB3',
      fontSize: '18px',
      fontWeight: 'bold',
      textTransform: 'none',
    },
    selected: {
      color: themeColor || DEFAULT_THEME_COLOR,
    },
  }))(Tab);
  return <CustomTab {...props}>{children}</CustomTab>;
};
StyledTab.propTypes = {
  children: PropTypes.node,
};
StyledTab.defaultProps = {
  children: null,
};

const StyledBreadcrumbs = styled(XtraBreadcrumbs)`
  @media (max-width: ${BREAKPOINT}px) {
    max-width: 100vw;
    width: auto;
    flex-grow: 1;
    flex-shrink: 0;
  }
`;

const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: hidden;
  width: 100%;

  @supports (-webkit-touch-callout: none) {
    overflow: initial;
  }

  @media (min-width: ${BREAKPOINT}px) {
    height: 100%;
    padding-top: ${(props) => (props.reserveBreadcrumbSpace ? '48px' : '0')};
  }
`;

const MobileTimeZoneSwitch = styled(TimeZoneSwitch)`
  padding: 0 24px;

  @media (min-width: 960px) {
    display: none;
  }
`;

const BreadcrumbPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  /* for edge on windows */
  @media (min-width: ${BREAKPOINT}px) {
    width: ${({ showTrackList }) => (showTrackList ? 'calc(100% - 305px)' : '100%')};
  }
`;

const WebinarPageContainer = () => {
  const { t } = useTranslation();
  const liveWebinars = useSelector(selectAllLiveWebinars());
  const relevantTracks = useSelector(selectAllRelevantTracks());
  const intervalMinute = useSelector(selectWebinarInterval);
  const onDemandWebinars = useSelector(selectAllOnDemandWebinars());
  const isLoading = useSelector(selectIsWebinarsLoading);
  const locale = useSelector(selectLocale);
  const { activeTabKey, setActiveTabKey } = useContext(TabsContext);
  const { pageNames: { webinar, lobby }, features } = useSelector((state) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);

  const onTabClick = (_, selectedTab) => {
    setActiveTabKey(selectedTab);
  };

  useEffect(() => {
    window.trackingEventToFirebase(null, 'Main_Webinar', 'Enter', '');
  }, []);

  /**
   * @type {LiveWebinarListOptions}
   */
  const options = {
    multiTracks: {
      enable: Boolean(relevantTracks.length),
      multiTracksId: relevantTracks.map((track) => track.id),
    },
    timeLine: {
      enable: !features.includes('hideWebinarTimeLine'),
      intervalMinute: intervalMinute || 20,
    },
    daySelector: {
      enable: true,
    },
    webinarTabs: ['live', 'onDemand'],
  };

  const tabs = Object.entries({
    live: {
      title: t('webinar.time_framed', 'Agenda'),
      element: (<LiveListContainer webinars={liveWebinars} isLoading={isLoading} options={options} />),
      show: liveWebinars.length > 0,
    },
    onDemand: {
      title: t('webinar.on_demand', 'On Demand'),
      element: (<OnDemandListContainer webinars={onDemandWebinars} isLoading={isLoading} />),
      show: onDemandWebinars.length > 0,
    },
  })
    .filter(([key, value]) => options.webinarTabs.indexOf(key) !== -1 && value.show)
    .map(([, value]) => value);

  const showTabSwitcher = tabs.length > 1;

  const lobbyName = lobby?.[locale] || t('page.lobby', 'Lobby');
  const webinarName = webinar?.[locale] || t('page.webinar', 'Webinars');
  const showTrackList = !features.includes('hideWebinarTracks')
    && activeTabKey === WEBINAR_TAB_KEYS.AGENDA
    && Boolean(relevantTracks.length);

  return (
    <RootContainer>
      <CustomHelmet>
        <meta charSet="utf-8" />
        <title>{`${webinarName} | ${eventName}`}</title>
        <meta name="description" content={`${webinarName} page of ${eventName}.`} />
      </CustomHelmet>
      <ScrollableContainerWrapper WrapperContainer={PageWrapper}>
        {showTrackList && <LiveWebinarTracksList tracks={relevantTracks} />}
        <BreadcrumbPageContainer showTrackList={showTrackList}>
          <BreadcrumbTimeZoneContainer>
            <StyledBreadcrumbs
              paths={[{ name: lobbyName, to: Routes.lobby }, { name: webinarName }]}
            />
            <MobileTimeZoneSwitch />
          </BreadcrumbTimeZoneContainer>
          <MainContainer reserveBreadcrumbSpace={(showTabSwitcher && !options.multiTracks.enable)}>
            {showTabSwitcher && (
              <StyledTabsContainer
                value={activeTabKey}
                onChange={onTabClick}
                centered
              >
                {tabs.map((tab) => (
                  <StyledTab
                    key={tab.title}
                    label={tab.title}
                  />
                ))}
              </StyledTabsContainer>
            )}
            <PageContentContainer>
              {tabs.map((tab) => tab.element)[activeTabKey]}
            </PageContentContainer>
          </MainContainer>
        </BreadcrumbPageContainer>
      </ScrollableContainerWrapper>
    </RootContainer>
  );
};

export default () => (
  <TabsContextStateProvider>
    <WebinarPageContainer />
  </TabsContextStateProvider>
);
